import { useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import API from "../utils/Axios.utils";
import useAuth from "./useAuth";
import logger from "../utils/logger";
import { buildMenuFromIntelliconvo, getMenuForRole } from "../utils/links";

export const MENU_SOURCES = Object.freeze({ INTELLICONVO: "intelliconvo", FALLBACK: "fallback" });

const SIDEBAR_MENU_ENDPOINT = "/api/users/sidebar-menu";
// Menus change rarely; the backend also caches them per role.
const SIDEBAR_MENU_STALE_MS = 5 * 60 * 1000;

let lastLoggedKey = null;

const fetchSidebarMenu = async () => {
  const { data } = await API.get(SIDEBAR_MENU_ENDPOINT);
  return data;
};

/**
 * Sidebar links for the signed-in role, managed in Intelliconvo.
 *
 * Falls back to the built-in menus (utils/links.js) when the backend reports
 * a fallback, the request fails, or none of the Intelliconvo routes match a
 * page of ours. One request is shared by the sidebar, the page guard and the
 * home redirect.
 *
 * @returns {{ menu: Array, source: string, reason: string|null, isLoading: boolean }}
 */
const useSidebarMenu = () => {
  const { isAuthenticated, role, userId } = useAuth();

  const { data, error, isLoading } = useQuery({
    queryKey: ["sidebarMenu", userId, role],
    queryFn: fetchSidebarMenu,
    enabled: isAuthenticated && !!role,
    staleTime: SIDEBAR_MENU_STALE_MS,
    retry: 1,
  });

  const result = useMemo(() => {
    if (error) {
      return { menu: getMenuForRole(role), source: MENU_SOURCES.FALLBACK, reason: "request_failed", unknownRoutes: [] };
    }
    if (!data || data.source !== MENU_SOURCES.INTELLICONVO) {
      return { menu: getMenuForRole(role), source: MENU_SOURCES.FALLBACK, reason: data?.reason || null, unknownRoutes: [] };
    }
    const { links, unknownRoutes } = buildMenuFromIntelliconvo(role, data.menus);
    if (!links.length) {
      return { menu: getMenuForRole(role), source: MENU_SOURCES.FALLBACK, reason: "no_known_routes", unknownRoutes };
    }
    return { menu: links, source: MENU_SOURCES.INTELLICONVO, reason: null, unknownRoutes };
  }, [data, error, role]);

  // One log line per result (not per component using the hook), so the
  // console shows where the sidebar came from.
  useEffect(() => {
    if (isLoading || (!data && !error)) return;
    const logKey = `${role}|${result.source}|${result.reason}|${result.menu.map((l) => l.id).join(",")}`;
    if (logKey === lastLoggedKey) return;
    lastLoggedKey = logKey;
    if (error) {
      logger.warn(`[SidebarMenu] Could not load menus (${error.message}) — using built-in menus for "${role}".`);
    }
    if (result.unknownRoutes.length) {
      logger.warn(`[SidebarMenu] Ignoring Intelliconvo routes with no TalentEngine page: ${result.unknownRoutes.join(", ")}.`);
    }
    if (result.source === MENU_SOURCES.INTELLICONVO) {
      logger.info(`[SidebarMenu] Source: intelliconvo — ${result.menu.length} menu(s) for "${role}" (role id ${data.roleId}).`);
    } else {
      logger.warn(`[SidebarMenu] Source: fallback (${result.reason || "unknown"}) — built-in menus for "${role}".`);
    }
  }, [result, isLoading, data, error, role]);

  return { menu: result.menu, source: result.source, reason: result.reason, isLoading: isLoading && !error };
};

export default useSidebarMenu;
